/**
 * English translations
 */

import type { Translation } from './zh';

const enTranslations: Translation = {
    app: {
        title: 'BookmarkHarbor',
        allBookmarks: 'All Bookmarks',
    },
    sidebar: {
        title: 'Folders',
        empty: 'No folders yet',
        myBookmarks: 'My Bookmarks',
        favorites: 'Favorites',
        readingList: 'Reading List',
        trash: 'Trash',
        folders: 'Folders',
        user: 'Local User',
        localMode: 'Local Mode',
    },
    toolbar: {
        new: 'New',
        newFolder: 'New Folder',
        newBookmark: 'New Bookmark',
        import: 'Import',
        export: 'Export',
        manage: 'Manage',
        delete: 'Delete',
        selection: 'Selection',
        selectionMode: 'Selection Mode',
        selectAll: 'Select All',
        clearSelection: 'Clear Selection',
        invertSelection: 'Invert Selection',
        undo: 'Undo',
        redo: 'Redo',
        sort: 'Sort',
        sortBy: 'Sort By',
        sortDefault: 'Default order',
        sortByName: 'Name',
        sortByDate: 'Modified Date',
        sortByType: 'Type',
        order: 'Order',
        ascending: 'Ascending',
        descending: 'Descending',
        view: 'View',
    },
    viewMode: {
        list: 'List View',
        card: 'Card View',
        tile: 'Tile View',
    },
    settings: {
        title: 'Settings',
        appearance: 'Appearance',
        behavior: 'Behavior',
        theme: 'Theme',
        language: 'Language',
        autoExpandTree: 'Auto expand folder tree',
        singleClickAction: 'Single click action',
        singleClickSelect: 'Select',
        singleClickOpen: 'Open',
        cardColumns: 'Card view columns',
        tileColumns: 'Tile view columns',
        columnsDesktop: 'Desktop',
        columnsMobile: 'Mobile',
        cardFolderPreviewSize: 'Folder preview size',
        defaultView: 'Default view',
        rememberFolderView: 'Remember folder view',
        themeColor: 'Theme color',
        customColor: 'Custom color',
        clearData: 'Clear data',
        clearDataDescription: 'Delete all bookmarks, folders, favorites, and settings. This cannot be undone.',
    },
    theme: {
        light: 'Light',
        dark: 'Dark',
        system: 'System',
    },
    language: {
        zh: '中文',
        en: 'English',
    },
    inspector: {
        title: 'Properties',
        preview: 'Preview',
        name: 'Name',
        url: 'URL',
        color: 'Color',
        notes: 'Notes',
        type: 'Type',
        created: 'Created',
        updated: 'Modified',
        folder: 'Folder',
        bookmark: 'Bookmark',
        changeCover: 'Change Cover',
        fetchMetadata: 'Auto Fetch',
        pasteUrl: 'Paste image URL...',
        close: 'Close',
        customColor: 'Custom color',
    },
    search: {
        placeholder: 'Search bookmarks...',
        noResults: 'No bookmarks found',
        scopeAll: 'All',
        scopeCurrent: 'Current Folder',
    },
    empty: {
        title: 'No Content',
        description: 'Click the buttons above to create a folder or bookmark',
        noBookmarks: 'No bookmarks yet',
    },
    content: {
        subfolders: 'Subfolders',
        bookmarks: 'Bookmarks',
        items: 'items',
    },
    contextMenu: {
        open: 'Open',
        openInNewTab: 'Open in New Tab',
        rename: 'Rename',
        delete: 'Delete',
        moveTo: 'Move to...',
        copyUrl: 'Copy URL',
        edit: 'Edit',
    },
    dialog: {
        confirm: 'Confirm',
        cancel: 'Cancel',
        save: 'Save',
        delete: 'Delete',
        deleteConfirm: 'Are you sure you want to delete {{count}} item(s)?',
        deleteWarning: 'This action cannot be undone',
        clearData: 'Clear all data',
        clearDataConfirm: 'This clears all bookmarks, folders, favorites, and settings stored in your browser.',
        clearDataSaveWarning: 'To keep your bookmarks, export an HTML file with Import / Export first.',
    },
    import: {
        title: 'Import Bookmarks',
        selectFiles: 'Select Files',
        dropHint: 'Drop HTML files here',
        targetFolder: 'Import to',
        currentFolder: 'Current Folder',
        selectFolder: 'Select Folder',
        importing: 'Importing...',
        success: 'Import Successful',
        created: 'Created {{count}} item(s)',
        errors: '{{count}} error(s) occurred',
    },
    export: {
        title: 'Export Bookmarks',
        all: 'All Bookmarks',
        currentFolder: 'Current Folder',
        selection: 'Selected Items',
        exporting: 'Exporting...',
        success: 'Export Successful',
    },
    error: {
        unknown: 'Unknown error',
        networkError: 'Network error',
        corsBlocked: 'Cannot access this website (CORS restriction)',
        fetchTimeout: 'Request timeout',
        invalidUrl: 'Invalid URL',
        storageFull: 'Storage is full',
    },
    selection: {
        selected: 'Selected',
        favorite: 'Favorite',
        readLater: 'Later',
        restore: 'Restore',
        deletePermanent: 'Delete Permanently',
        delete: 'Delete',
    },
    toast: {
        copied: 'Copied to clipboard',
        saved: 'Saved',
        deleted: 'Deleted {{count}} item(s)',
        deletedPermanent: 'Permanently deleted {{count}} item(s)',
        moved: 'Moved',
        renamed: 'Renamed',
        undo: 'Undo',
        favorited: 'Added to favorites',
        unfavorited: 'Removed from favorites',
        readLaterAdded: 'Added to read later',
        readLaterRemoved: 'Removed from read later',
        restored: 'Restored {{count}} item(s)',
        exportSelectionEmpty: 'Select items to export',
        cleared: 'All data cleared',
    },
    aria: {
        toggleSidebar: 'Toggle sidebar',
        toggleInspector: 'Toggle properties panel',
        switchLanguage: 'Switch language',
        switchTheme: 'Switch theme',
        clearSearch: 'Clear search',
        breadcrumbOverflow: 'Show collapsed path',
        listView: 'List view',
        cardView: 'Card view',
        tileView: 'Tile view',
        close: 'Close',
        expand: 'Expand',
        collapse: 'Collapse',
        skipToContent: 'Skip to content',
        openSettings: 'Open settings',
        resizeSidebar: 'Resize sidebar width',
        resizeInspector: 'Resize properties panel width',
    },
};

export default enTranslations;
